import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Row,
  Col,
  Tag,
  Drawer,
  Descriptions,
  Divider,
  Space,
  Typography,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  HomeOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  EditOutlined,
  EyeOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import {
  getBranches,
  createBranch,
  updateBranch,
  deleteBranch,
  getZatcaCrProfiles,
  getRegions,
  getCities,
} from "../../../network/network";

import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";

import { useTranslation } from "react-i18next";

import {
  arabicNameRules,
  cleanArabicInput,
  fourDigitNumberRules,
  postalCodeRules,
} from "../../../utils/validators";

import { AppContext } from "../../../context/AppContextProvider";
import {
  AddonName,
  ZatcaBranchOnboardingStatus,
} from "../../../configs/constants";
import { handleApiError } from "../../../utils/errorHandler";
import { getTableRowNumber } from "../../../utils/tablePagination";

const ADD_BRANCH = "Add Branch";

const ZATCA_PROFILE_FIELDS = [
  "business_arabic_name",
  "street_name",
  "building_number",
  "plot_identification",
  "district",
  "region_id",
  "city_id",
  "postal_zone",
];

const ZATCA_REONBOARD_FIELDS = ["cr", ...ZATCA_PROFILE_FIELDS];

const getBranchCr = (branch) => branch?.cr || branch?.cr_number || "";
const getBranchProfile = (branch) => branch?.zatca_profile || {};
const getProfileRegionId = (profile) =>
  profile?.region_id || profile?.region?.id || profile?.city?.region_id;
const getProfileCityId = (profile) => profile?.city_id || profile?.city?.id;
const getBranchRegionId = (branch) => branch?.business_region?.id;
const getBranchCityId = (branch) => branch?.business_city?.id;
const getBranchPostalCode = (branch) => branch?.business_postal_code || "";
const emptyText = (value) => value || "-";

const Branches = () => {
  const { authUser, appConfig } = useContext(AppContext);

  const [loadingBTN, setLoadingBTN] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [branchesTableData, setBranchesTableData] = useState([]);
  const [zatcaCrProfiles, setZatcaCrProfiles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [zatcaCities, setZatcaCities] = useState([]);
  const [zatcaCitiesLoading, setZatcaCitiesLoading] = useState(false);
  const [modalTitle, setModalTitle] = useState(ADD_BRANCH);
  const [editingBranch, setEditingBranch] = useState(null);

  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);

  const [addBranchForm] = Form.useForm();

  const { t } = useTranslation();

  const is_zatca_e_invoicing_active = authUser?.active_addons?.some(
    (addon) => addon?.id && addon?.name === AddonName.ZatcaEInvoicing,
  );

  const useExistingZatcaProfile = Form.useWatch(
    "use_existing_branch_zatca",
    addBranchForm,
  );
  const selectedBusinessRegionId = Form.useWatch(
    "business_region_id",
    addBranchForm,
  );
  const selectedZatcaRegionId = Form.useWatch("region_id", addBranchForm);

  const isAddMode = modalTitle === ADD_BRANCH || modalTitle === t("branch_add");

  const crProfilesById = useMemo(() => {
    return zatcaCrProfiles.reduce((profiles, profile) => {
      if (profile?.id) {
        profiles[profile.id] = profile;
      }

      return profiles;
    }, {});
  }, [zatcaCrProfiles]);

  const selectedCrProfile = useMemo(() => {
    if (!selectedBranch?.zatca_cr_profile_id) return null;

    return crProfilesById[selectedBranch.zatca_cr_profile_id] || null;
  }, [crProfilesById, selectedBranch]);

  const selectedProfile = selectedCrProfile || getBranchProfile(selectedBranch);

  const getLocalizedName = (entity) => {
    if (!entity) return "";

    return appConfig.direction === "rtl"
      ? entity.name_ar || entity.name_en || entity.name || ""
      : entity.name_en || entity.name_ar || entity.name || "";
  };

  const getBranchRegionDisplayName = (branch) => {
    const regionId = getBranchRegionId(branch);
    const region =
      branch?.business_region ||
      regions.find((item) => String(item.id) === String(regionId));

    return getLocalizedName(region) || regionId;
  };

  const getBranchCityDisplayName = (branch) => {
    const cityId = getBranchCityId(branch);
    const city =
      branch?.business_city ||
      cities.find((item) => String(item.id) === String(cityId));

    return getLocalizedName(city) || branch?.business_city_name || cityId;
  };

  const getRegionDisplayName = (profile) => {
    const regionId = getProfileRegionId(profile);
    const region =
      profile?.region ||
      regions.find((item) => String(item.id) === String(regionId));

    return getLocalizedName(region) || regionId;
  };

  const getCityDisplayName = (profile) => {
    const cityId = getProfileCityId(profile);
    const city =
      profile?.city ||
      zatcaCities.find((item) => String(item.id) === String(cityId));

    return getLocalizedName(city) || profile?.city_name || cityId;
  };

  const regionOptions = useMemo(
    () =>
      regions.map((region) => ({
        value: region.id,
        label: getLocalizedName(region),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [regions, appConfig.direction],
  );

  const cityOptions = useMemo(
    () =>
      cities.map((city) => ({
        value: city.id,
        label: getLocalizedName(city),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cities, appConfig.direction],
  );

  const zatcaCityOptions = useMemo(
    () =>
      zatcaCities.map((city) => ({
        value: city.id,
        label: getLocalizedName(city),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zatcaCities, appConfig.direction],
  );

  const filterByLabel = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const fetchCitiesByRegion = (regionId) => {
    if (!regionId) {
      setCities([]);
      setCitiesLoading(false);
      return;
    }

    setCitiesLoading(true);
    getCities(regionId)
      .then((response) => {
        if (response?.data?.success === false) {
          setCities([]);
          handleApiError(response, t);
          return;
        }

        setCities(response?.data?.data?.cities || []);
      })
      .catch((error) => {
        setCities([]);
        handleApiError(error, t);
      })
      .finally(() => {
        setCitiesLoading(false);
      });
  };

  const handleBusinessRegionSelect = (regionId) => {
    addBranchForm.resetFields(["business_city_id"]);
    fetchCitiesByRegion(regionId);
  };

  const fetchZatcaCitiesByRegion = (regionId) => {
    if (!regionId) {
      setZatcaCities([]);
      setZatcaCitiesLoading(false);
      return;
    }

    setZatcaCitiesLoading(true);
    getCities(regionId)
      .then((response) => {
        if (response?.data?.success === false) {
          setZatcaCities([]);
          handleApiError(response, t);
          return;
        }

        setZatcaCities(response?.data?.data?.cities || []);
      })
      .catch((error) => {
        setZatcaCities([]);
        handleApiError(error, t);
      })
      .finally(() => {
        setZatcaCitiesLoading(false);
      });
  };

  const handleZatcaRegionSelect = (regionId) => {
    addBranchForm.resetFields(["city_id"]);
    fetchZatcaCitiesByRegion(regionId);
  };

  useEffect(() => {
    setIsDataLoading(true);

    getBranches({
      page: currentPage,
      per_page: perPageSize,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        const data = response?.data?.data || response?.data;

        setBranchesTableData(data?.branches || []);
        setPaginationData(data?.pagination || {});
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, currentPage, perPageSize, t]);

  useEffect(() => {
    getRegions()
      .then((response) => {
        if (response?.data?.success === false) {
          setRegions([]);
          handleApiError(response, t);
          return;
        }

        setRegions(response?.data?.data?.regions || []);
      })
      .catch((error) => {
        setRegions([]);
        handleApiError(error, t);
      });
  }, [t]);

  useEffect(() => {
    if (!is_zatca_e_invoicing_active) {
      setZatcaCrProfiles([]);
      return;
    }

    getZatcaCrProfiles()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          setZatcaCrProfiles([]);
          return;
        }

        const data = response?.data?.data || response?.data;

        setZatcaCrProfiles(data?.profiles || []);
      })
      .catch((error) => {
        handleApiError(error, t);
        setZatcaCrProfiles([]);
      });
  }, [is_zatca_e_invoicing_active, reloadTableData, t]);

  const getZatcaStatus = (branch) => {
    const status = branch?.zatca_onboarding_status;

    if (Object.values(ZatcaBranchOnboardingStatus).includes(status)) {
      return status;
    }

    return ZatcaBranchOnboardingStatus.NotStarted;
  };

  const renderZatcaStatusTag = (branch) => {
    const status = getZatcaStatus(branch);

    const statusConfig = {
      [ZatcaBranchOnboardingStatus.NotStarted]: {
        color: "default",
        label: t("not_started"),
      },
      [ZatcaBranchOnboardingStatus.InProgress]: {
        color: "processing",
        label: t("in_progress"),
      },
      [ZatcaBranchOnboardingStatus.Onboarded]: {
        color: "success",
        label: t("onboarded"),
      },
      [ZatcaBranchOnboardingStatus.NeedsReonboarding]: {
        color: "warning",
        label: t("needs_reonboarding"),
      },
      [ZatcaBranchOnboardingStatus.Failed]: {
        color: "error",
        label: t("failed"),
      },
    };

    const config = statusConfig[status];

    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const existingCrProfileOptions = useMemo(
    () =>
      zatcaCrProfiles
        .filter((profile) => profile?.id && profile?.is_complete !== false)
        .map((profile) => ({
          value: profile.id,
          label: `${profile.cr || "-"} - ${
            profile.business_arabic_name || t("zatca_cr_profile")
          }`,
        })),
    [t, zatcaCrProfiles],
  );

  const handleArabicNameChange = (event) => {
    addBranchForm.setFieldsValue({
      business_arabic_name: cleanArabicInput(event.target.value),
    });
  };

  const showModal = () => {
    setEditingBranch(null);
    setModalVisible(true);
    setModalTitle(t("branch_add"));

    addBranchForm.resetFields();
    setCities([]);
    setZatcaCities([]);
    addBranchForm.setFieldsValue({
      use_existing_branch_zatca: false,
    });
  };

  const handleAPIResponse = (response, type) => {
    if (response.data.success === true) {
      setReloadTableData((prev) => !prev);

      NotificationWithIcon(
        "success",
        type === ADD_BRANCH ? t("branch_added") : t("branch_updated"),
        type === ADD_BRANCH
          ? t("branch_added_successfully")
          : t("branch_updated_successfully"),
      );

      addBranchForm.resetFields();
      setEditingBranch(null);
      setModalVisible(false);
    } else {
      handleApiError(response, t);
    }
  };

  const buildPayload = (values) => {
    const payload = {
      id: values.id,
      name: values.name,
      business_address: values.business_address,
      business_postal_code: values.business_postal_code,
      business_region_id: values.business_region_id,
      business_city_id: values.business_city_id,
      code: values.code,
    };

    if (!is_zatca_e_invoicing_active) return payload;

    if (isAddMode && values.use_existing_branch_zatca === true) {
      return {
        ...payload,
        zatca_cr_profile_id: values.zatca_cr_profile_id,
      };
    }

    return {
      ...payload,
      cr: values.cr,
      zatca_profile: ZATCA_PROFILE_FIELDS.reduce((profile, field) => {
        profile[field] = values[field];
        return profile;
      }, {}),
    };
  };

  const hasZatcaIdentityChanged = (values) => {
    if (!editingBranch) return false;

    const profile = getBranchProfile(editingBranch);

    return ZATCA_REONBOARD_FIELDS.some((field) => {
      let oldFieldValue = profile?.[field];

      if (field === "cr") {
        oldFieldValue = getBranchCr(editingBranch);
      }

      if (field === "region_id") {
        oldFieldValue = getProfileRegionId(profile);
      }

      if (field === "city_id") {
        oldFieldValue = getProfileCityId(profile);
      }

      const oldValue = String(oldFieldValue || "");
      const newValue = String(values?.[field] ?? "");

      return oldValue !== newValue;
    });
  };

  const submitBranch = (values) => {
    setLoadingBTN(true);

    const payload = buildPayload(values);
    const request = isAddMode ? createBranch(payload) : updateBranch(payload);

    request
      .then((response) =>
        handleAPIResponse(response, isAddMode ? ADD_BRANCH : null),
      )
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoadingBTN(false);
      });
  };

  const handleAddEdit = () => {
    addBranchForm
      .validateFields()
      .then((values) => {
        const zatcaStatus = getZatcaStatus(editingBranch);

        if (
          !isAddMode &&
          [
            ZatcaBranchOnboardingStatus.Onboarded,
            ZatcaBranchOnboardingStatus.NeedsReonboarding,
          ].includes(zatcaStatus) &&
          hasZatcaIdentityChanged(values)
        ) {
          Modal.confirm({
            title: t("zatca_reonboarding_confirm_title"),
            content: t("zatca_reonboarding_confirm_message"),
            okText: t("confirm"),
            cancelText: t("cancel"),
            onOk: () => submitBranch(values),
          });

          return;
        }

        submitBranch(values);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleEditModal = (id) => {
    const branch = branchesTableData.find((branch) => branch.id === id);
    const profile = getBranchProfile(branch);

    setEditingBranch(branch);
    setModalTitle(t("branch_edit") + " : " + branch.name);
    setModalVisible(true);

    const businessRegionId = getBranchRegionId(branch);
    const zatcaRegionId = getProfileRegionId(profile);

    addBranchForm.resetFields();
    addBranchForm.setFieldsValue({
      id: branch.id,
      name: branch.name,
      business_address: branch.business_address,
      business_postal_code: getBranchPostalCode(branch),
      business_region_id: businessRegionId,
      business_city_id: getBranchCityId(branch),
      code: branch.code,
      cr: getBranchCr(branch),
      business_arabic_name: profile.business_arabic_name,
      street_name: profile.street_name,
      building_number: profile.building_number,
      plot_identification: profile.plot_identification,
      district: profile.district,
      region_id: zatcaRegionId,
      city_id: getProfileCityId(profile),
      postal_zone: profile.postal_zone,
    });

    fetchCitiesByRegion(businessRegionId);
    fetchZatcaCitiesByRegion(zatcaRegionId);
  };

  const handleCancel = () => {
    addBranchForm.resetFields();
    setCities([]);
    setZatcaCities([]);
    setEditingBranch(null);
    setModalVisible(false);
  };

  const handleDelete = (id) => {
    deleteBranch(id)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData((prev) => !prev);

          NotificationWithIcon(
            "success",
            t("branch_deleted"),
            t("branch_deleted_successfully"),
          );
        } else if (response?.data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const openDetailsDrawer = (branch) => {
    setSelectedBranch(branch);
    setDetailsDrawerVisible(true);
  };

  const closeDetailsDrawer = () => {
    setSelectedBranch(null);
    setDetailsDrawerVisible(false);
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "branches",
      breadcrumbName: t("branches"),
      icon: <EnvironmentOutlined />,
    },
  ];

  const detailsPartColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => (
        <p>{getTableRowNumber(index, paginationData)}</p>
      ),
    },
    {
      title: t("name"),
      dataIndex: "name",
      render: (value, record) => (
        <>
          <div>{value}</div>
          {is_zatca_e_invoicing_active && renderZatcaStatusTag(record)}
        </>
      ),
    },
    {
      title: t("address"),
      dataIndex: "business_address",
    },
    {
      title: t("cr_number"),
      render: (_, record) => getBranchCr(record) || "-",
    },

    {
      title: t("code"),
      dataIndex: "code",
      render: (value) => value || "-",
    },
  ];

  const actionPartColumn = [
    {
      title: t("action"),
      width: 85,
      align: "center",
      render: (record) => (
        <Space>
          {is_zatca_e_invoicing_active && (
            <Button
              type="link"
              onClick={() => openDetailsDrawer(record)}
              title={t("view_details")}
            >
              <EyeOutlined />
            </Button>
          )}

          <Button
            type="link"
            onClick={() => handleEditModal(record.id)}
            title={t("branch_edit")}
          >
            <EditOutlined />
          </Button>
          <Popconfirm
            title={t("warning_delete")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("yes")}
            cancelText={t("no")}
            placement="topLeft"
          >
            <Button type="link" title={t("delete_entity")}>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  const shouldShowZatcaFields = !isAddMode || useExistingZatcaProfile === false;

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />

        <PageHeader
          title={t("branches")}
          ghost={false}
          className="app-p-0"
          extra={[
            <Button
              key="open-add-store-modal"
              type="primary"
              onClick={showModal}
            >
              + {t("branch_add")}
            </Button>,
          ]}
        />
      </Card>

      <Card>
        <Table
          columns={[...detailsPartColumns, ...actionPartColumn]}
          dataSource={branchesTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={
            paginationData.total < 10
              ? false
              : {
                  current: paginationData.current_page || currentPage,
                  pageSize: Number(paginationData.per_page) || perPageSize,
                  total: paginationData.total || 0,
                  showSizeChanger: true,
                }
          }
          onChange={handleTableChange}
        />
      </Card>

      <Drawer
        title={modalTitle}
        placement={appConfig.direction === "rtl" ? "left" : "right"}
        onClose={handleCancel}
        open={modalVisible}
        width={1000}
        closable={false}
        footer={
          <Space>
            <Button
              type="default"
              key="back"
              loading={loadingBTN}
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={loadingBTN}
              onClick={handleAddEdit}
            >
              {isAddMode ? t("branch_add") : t("update")}
            </Button>
          </Space>
        }
        extra={
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={handleCancel}
          />
        }
      >
        <Form
          name="add-branch-form"
          layout="vertical"
          onFinish={handleAddEdit}
          className="app-form-full"
          form={addBranchForm}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label={t("name")}
                rules={[
                  {
                    required: true,
                    message: t("validate_name_required"),
                    whitespace: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item name="code" label={t("code")}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 8, marginBottom: 16 }}>
            <Typography.Title
              level={4}
              style={{ margin: 0, fontSize: 17, fontWeight: 600 }}
            >
              {t("branch_address_details")}
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {t("branch_address_details_helper")}
            </Typography.Text>
          </div>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="business_region_id"
                label={t("region")}
                rules={[
                  {
                    required: true,
                    message: t("validate_region_required"),
                  },
                ]}
                hasFeedback
              >
                <Select
                  showSearch
                  options={regionOptions}
                  onChange={handleBusinessRegionSelect}
                  filterOption={filterByLabel}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="business_city_id"
                label={t("city")}
                rules={[
                  {
                    required: true,
                    message: t("validate_city_required"),
                  },
                ]}
                hasFeedback
              >
                <Select
                  showSearch
                  loading={citiesLoading}
                  disabled={!selectedBusinessRegionId || citiesLoading}
                  options={cityOptions}
                  filterOption={filterByLabel}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="business_postal_code"
                label={t("postal_code")}
                validateFirst
                validateTrigger={["onBlur", "onChange"]}
                rules={postalCodeRules(t, true)}
                hasFeedback
              >
                <Input maxLength={5} inputMode="numeric" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                name="business_address"
                label={t("address")}
                rules={[
                  {
                    required: true,
                    message: t("validate_address"),
                    whitespace: true,
                  },
                ]}
                hasFeedback
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {is_zatca_e_invoicing_active && (
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              <Typography.Title
                level={4}
                style={{ margin: 0, fontSize: 17, fontWeight: 600 }}
              >
                {t("zatca_profile_details")}
              </Typography.Title>
            </div>
          )}

          {is_zatca_e_invoicing_active && isAddMode && (
            <>
              <Form.Item
                name="use_existing_branch_zatca"
                label={t("use_existing_branch_zatca")}
                rules={[
                  {
                    required: true,
                    message: t("validate_use_existing_branch_zatca"),
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value={true}>{t("use_existing_cr_number")}</Radio>
                  <Radio value={false}>{t("add_new_cr_number")}</Radio>
                </Radio.Group>
              </Form.Item>

              {useExistingZatcaProfile === true && (
                <Form.Item
                  name="zatca_cr_profile_id"
                  label={t("select_zatca_cr_profile")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_select_zatca_cr_profile"),
                    },
                  ]}
                >
                  <Select
                    options={existingCrProfileOptions}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}
            </>
          )}

          {is_zatca_e_invoicing_active && shouldShowZatcaFields && (
            <Row gutter={16}>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="cr"
                  label={t("cr_number")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_cr_number_required"),
                    },
                    {
                      pattern: /^\d{10}$/,
                      message: t("validate_cr_number_exact_limit"),
                    },
                  ]}
                >
                  <Input maxLength={10} inputMode="numeric" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="business_arabic_name"
                  label={t("business_name_arabic")}
                  rules={arabicNameRules(t, true)}
                >
                  <Input
                    dir="rtl"
                    onChange={handleArabicNameChange}
                    placeholder={t("arabic_only_validation")}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="street_name"
                  label={t("street_name")}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t("validate_street_name_required"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="building_number"
                  label={t("building_number")}
                  rules={fourDigitNumberRules(
                    t,
                    true,
                    "validate_building_number_required",
                    "validate_building_number_4digits",
                  )}
                >
                  <Input maxLength={4} inputMode="numeric" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="plot_identification"
                  label={t("plot_identification")}
                  rules={fourDigitNumberRules(
                    t,
                    true,
                    "validate_plot_identification_required",
                    "validate_plot_identification_4digits",
                  )}
                >
                  <Input maxLength={4} inputMode="numeric" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="district"
                  label={t("district")}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: t("validate_district_required"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="region_id"
                  label={t("region")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_region_required"),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    options={regionOptions}
                    onChange={handleZatcaRegionSelect}
                    filterOption={filterByLabel}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="city_id"
                  label={t("city")}
                  rules={[
                    {
                      required: true,
                      message: t("validate_city_required"),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    loading={zatcaCitiesLoading}
                    disabled={!selectedZatcaRegionId || zatcaCitiesLoading}
                    options={zatcaCityOptions}
                    filterOption={filterByLabel}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="postal_zone"
                  label={t("postal_code")}
                  rules={postalCodeRules(t, true)}
                >
                  <Input maxLength={5} inputMode="numeric" />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item name="id" className="app-hidden">
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title={t("zatca_branch_details")}
        placement={appConfig.direction === "rtl" ? "left" : "right"}
        open={detailsDrawerVisible}
        onClose={closeDetailsDrawer}
        width={680}
        destroyOnClose
        closable={false}
        extra={
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={closeDetailsDrawer}
          />
        }
      >
        {selectedBranch && (
          <>
            <Descriptions
              title={t("branch_information")}
              bordered
              column={1}
              size="small"
            >
              <Descriptions.Item label={t("name")}>
                {emptyText(selectedBranch.name)}
              </Descriptions.Item>

              <Descriptions.Item label={t("code")}>
                {emptyText(selectedBranch.code)}
              </Descriptions.Item>

              <Descriptions.Item label={t("region")}>
                {emptyText(getBranchRegionDisplayName(selectedBranch))}
              </Descriptions.Item>

              <Descriptions.Item label={t("city")}>
                {emptyText(getBranchCityDisplayName(selectedBranch))}
              </Descriptions.Item>

              <Descriptions.Item label={t("postal_code")}>
                {emptyText(getBranchPostalCode(selectedBranch))}
              </Descriptions.Item>

              <Descriptions.Item label={t("address")}>
                {emptyText(selectedBranch.business_address)}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Descriptions
              title={t("zatca_profile_details")}
              bordered
              column={1}
              size="small"
            >
              <Descriptions.Item label={t("zatca_cr_profile")}>
                {emptyText(selectedBranch.zatca_cr_profile_id)}
              </Descriptions.Item>

              <Descriptions.Item label={t("cr_number")}>
                {emptyText(
                  selectedCrProfile?.cr || getBranchCr(selectedBranch),
                )}
              </Descriptions.Item>

              <Descriptions.Item label={t("zatca_status")}>
                {renderZatcaStatusTag(selectedBranch)}
              </Descriptions.Item>

              <Descriptions.Item label={t("profile_completed")}>
                {selectedCrProfile?.is_complete ? (
                  <Tag color="success">{t("yes")}</Tag>
                ) : (
                  <Tag color="warning">{t("no")}</Tag>
                )}
              </Descriptions.Item>

              <Descriptions.Item label={t("branches_count")}>
                {emptyText(selectedCrProfile?.branches_count)}
              </Descriptions.Item>

              <Descriptions.Item label={t("business_name_arabic")}>
                <span dir="rtl">
                  {emptyText(selectedProfile?.business_arabic_name)}
                </span>
              </Descriptions.Item>

              <Descriptions.Item label={t("street_name")}>
                {emptyText(selectedProfile?.street_name)}
              </Descriptions.Item>

              <Descriptions.Item label={t("building_number")}>
                {emptyText(selectedProfile?.building_number)}
              </Descriptions.Item>

              <Descriptions.Item label={t("plot_identification")}>
                {emptyText(selectedProfile?.plot_identification)}
              </Descriptions.Item>

              <Descriptions.Item label={t("district")}>
                {emptyText(selectedProfile?.district)}
              </Descriptions.Item>

              <Descriptions.Item label={t("region")}>
                {emptyText(getRegionDisplayName(selectedProfile))}
              </Descriptions.Item>

              <Descriptions.Item label={t("city")}>
                {emptyText(getCityDisplayName(selectedProfile))}
              </Descriptions.Item>

              <Descriptions.Item label={t("postal_code")}>
                {emptyText(selectedProfile?.postal_zone)}
              </Descriptions.Item>
            </Descriptions>

            {!!selectedCrProfile?.branches?.length && (
              <>
                <Divider />

                <Descriptions
                  title={t("linked_branches")}
                  bordered
                  column={1}
                  size="small"
                >
                  <Descriptions.Item label={t("branches")}>
                    <Space wrap>
                      {selectedCrProfile.branches.map((branch) => (
                        <Tag key={branch.id}>
                          {branch.name}
                          {branch.code ? ` (${branch.code})` : ""}
                        </Tag>
                      ))}
                    </Space>
                  </Descriptions.Item>
                </Descriptions>
              </>
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

export default Branches;
